import { getProviderSelectionLabelFromProvider } from "../skill-presets";
import type { SkillCollectionReviewItem } from "../store";

interface BuildVersionReviewItemsParams {
  previousFiles: Record<string, string>;
  nextFiles: Record<string, string>;
  previousProvider?: string | null;
  nextProvider?: string | null;
  previousFilePaths?: string[] | null;
  nextFilePaths?: string[] | null;
}

const getEmptyFolderPaths = (paths?: string[] | null): string[] => {
  if (!paths || paths.length === 0) {
    return [];
  }

  const filePaths = paths.filter(path => !path.endsWith("/"));

  return paths
    .filter(path => path.endsWith("/"))
    .map(path => path.slice(0, -1))
    .filter(folderPath => !filePaths.some(filePath => filePath.startsWith(`${folderPath}/`)))
    .sort((a, b) => a.localeCompare(b));
};

export const buildProviderReviewItem = (
  previousProvider: string | null | undefined,
  nextProvider: string | null | undefined,
): SkillCollectionReviewItem | null => {
  if (previousProvider === nextProvider) {
    return null;
  }

  return {
    key: "modified:provider",
    changeType: "modified",
    path: "provider",
    displayLabel: "Provider",
    originalContent: getProviderSelectionLabelFromProvider(previousProvider),
    currentContent: getProviderSelectionLabelFromProvider(nextProvider),
    requiresOriginalFetch: false,
  };
};

export const isProviderReviewItem = (item: { path: string; displayLabel?: string }) =>
  item.path === "provider" && item.displayLabel === "Provider";

export const buildVersionReviewItems = ({
  previousFiles,
  nextFiles,
  previousProvider,
  nextProvider,
  previousFilePaths,
  nextFilePaths,
}: BuildVersionReviewItemsParams): SkillCollectionReviewItem[] => {
  const items: SkillCollectionReviewItem[] = [];
  const providerReviewItem = buildProviderReviewItem(previousProvider, nextProvider);
  if (providerReviewItem) {
    items.push(providerReviewItem);
  }
  const allPaths = new Set([...Object.keys(previousFiles), ...Object.keys(nextFiles)]);

  for (const path of Array.from(allPaths).sort((a, b) => a.localeCompare(b))) {
    const originalContent = previousFiles[path];
    const currentContent = nextFiles[path];

    if (originalContent === undefined && currentContent !== undefined) {
      items.push({
        key: `added:${path}`,
        changeType: "added",
        path,
        currentContent,
        requiresOriginalFetch: false,
      });
      continue;
    }

    if (originalContent !== undefined && currentContent === undefined) {
      items.push({
        key: `deleted:${path}`,
        changeType: "deleted",
        path,
        oldPath: path,
        originalContent,
        requiresOriginalFetch: false,
      });
      continue;
    }

    if (
      originalContent !== undefined &&
      currentContent !== undefined &&
      originalContent !== currentContent
    ) {
      items.push({
        key: `modified:${path}`,
        changeType: "modified",
        path,
        originalContent,
        currentContent,
        requiresOriginalFetch: false,
      });
    }
  }

  const previousEmptyFolders = new Set(getEmptyFolderPaths(previousFilePaths));
  const nextEmptyFolders = new Set(getEmptyFolderPaths(nextFilePaths));

  for (const path of Array.from(nextEmptyFolders).sort((a, b) => a.localeCompare(b))) {
    if (!previousEmptyFolders.has(path)) {
      items.push({
        key: `added-folder:${path}`,
        changeType: "added",
        path: `${path}/`,
        requiresOriginalFetch: false,
        isEmptyFolder: true,
      });
    }
  }

  for (const path of Array.from(previousEmptyFolders).sort((a, b) => a.localeCompare(b))) {
    if (!nextEmptyFolders.has(path)) {
      items.push({
        key: `deleted-folder:${path}`,
        changeType: "deleted",
        path: `${path}/`,
        oldPath: `${path}/`,
        requiresOriginalFetch: false,
        isEmptyFolder: true,
      });
    }
  }

  return items;
};
